import { Platform } from 'react-native'
import { Easing, Keyframe, LinearTransition } from 'react-native-reanimated'

/**
 * An entering/exiting/layout animation, or nothing under react-native-web.
 *
 * Reanimated's web backend pins an animating view to `position: absolute` with
 * a measured top/left and never restores it, so the view leaves its slot: the
 * row it was in collapses and whatever clipped that row hides it. A static but
 * correct layout beats an animated broken one on a platform the SDK does not
 * target.
 */
export function nativeMotion<T>(animation: T): T | undefined {
  return Platform.OS === 'web' ? undefined : animation
}

// One clock for the amount screen: fast enough that a quick run on the numpad
// never leaves glyphs queued mid-slide, slow enough to read as a slide.
export const MOTION_DURATION = 180
export const MOTION_EASING = Easing.bezier(0.33, 1, 0.68, 1)
/** The clock as a `withTiming` config, for animations driven outside keyframes. */
export const MOTION_TIMING = {
  duration: MOTION_DURATION,
  easing: MOTION_EASING,
}

// A wait shorter than this never gets a spinner: a ring that appears and leaves
// again within a few frames reads as a glitch pulling the eye to the button.
export const SPINNER_APPEAR_DELAY_MS = 150

// A character arriving or leaving re-centres the number, so the ones staying
// slide to their new place rather than cutting to it.
export const GLYPH_LAYOUT =
  LinearTransition.duration(MOTION_DURATION).easing(MOTION_EASING)

// A new digit starts at the baseline, small and faint, and rises up into place
// while enlarging to full size (Figma "Typing Input"). Deleting — or a digit
// rolling to a new value — runs the same path backwards.
export const DIGIT_ENTER_SCALE = 0.5
export const DIGIT_TRAVEL_RATIO = 0.4

// Digits mounting together (a quick-amount chip) land one after another, like
// a fast typist — landing as one block reads as a glitch, not an entrance.
export const DIGIT_STAGGER = 40

/**
 * The digit mount/unmount pair for a line box `travel` points of rise. `delay`
 * holds the entrance back for staggered bulk mounts; exits always run at once.
 */
export function digitKeyframes(travel: number, delay = 0) {
  return {
    entering: new Keyframe({
      0: {
        opacity: 0,
        transform: [{ translateY: travel }, { scale: DIGIT_ENTER_SCALE }],
      },
      100: {
        opacity: 1,
        transform: [{ translateY: 0 }, { scale: 1 }],
        easing: MOTION_EASING,
      },
    })
      .duration(MOTION_DURATION)
      .delay(delay),
    exiting: new Keyframe({
      0: { opacity: 1, transform: [{ translateY: 0 }, { scale: 1 }] },
      100: {
        opacity: 0,
        transform: [{ translateY: travel }, { scale: DIGIT_ENTER_SCALE }],
        easing: MOTION_EASING,
      },
    }).duration(MOTION_DURATION),
  }
}

// transitions.dev's smooth ease-out: what its modal, page slide and success
// check all run on. Kept beside the amount clock rather than replacing it —
// the numpad's own timing is tuned to keystrokes, these to screens.
export const SMOOTH_EASING = Easing.bezier(0.22, 1, 0.36, 1)

/**
 * The deposit action changing phase: its fill crosses between phase colours on
 * the amount screen's own clock, and the label and spinner arrive once that
 * crossing is visibly under way rather than with it.
 */
export const CONTENT_FADE_DELAY = 60
export const CONTENT_FADE_DURATION = 120
export const CONTENT_FADE_TIMING = {
  duration: CONTENT_FADE_DURATION,
  easing: MOTION_EASING,
}

/**
 * A keyboard action rising into place. Deposit amount fills use
 * DepositActionLayers so state changes can replay without remounting.
 */
export const RISE_TRAVEL = 24
export const RISE_ENTERING = new Keyframe({
  0: { opacity: 0, transform: [{ translateY: RISE_TRAVEL }] },
  100: { opacity: 1, transform: [{ translateY: 0 }], easing: MOTION_EASING },
}).duration(MOTION_DURATION)
export const RISE_EXITING = new Keyframe({
  0: { opacity: 1, transform: [{ translateY: 0 }] },
  100: {
    opacity: 0,
    transform: [{ translateY: RISE_TRAVEL }],
    easing: MOTION_EASING,
  },
}).duration(MOTION_DURATION)

/**
 * transitions.dev "Modal open / close": a surface scales up from 0.96 over
 * 250ms and dips back to it over a quicker 150ms.
 */
export const MODAL_OPEN_DURATION = 250
export const MODAL_CLOSE_DURATION = 150
export const MODAL_SCALE = 0.96
export const MODAL_ENTERING = new Keyframe({
  0: { opacity: 0, transform: [{ scale: MODAL_SCALE }] },
  100: { opacity: 1, transform: [{ scale: 1 }], easing: SMOOTH_EASING },
}).duration(MODAL_OPEN_DURATION)
export const MODAL_EXITING = new Keyframe({
  0: { opacity: 1, transform: [{ scale: 1 }] },
  100: {
    opacity: 0,
    transform: [{ scale: MODAL_SCALE }],
    easing: SMOOTH_EASING,
  },
}).duration(MODAL_CLOSE_DURATION)

/**
 * transitions.dev "Page side-by-side": the page ahead sits 8px to the right,
 * the page behind 8px to the left, and a turn fades each across 250ms. The
 * blur it also runs is left out: React Native cannot animate a filter.
 */
export const PAGE_TURN_DURATION = 250
export const PAGE_SLIDE_DISTANCE = 8
export const PAGE_TIMING = {
  duration: PAGE_TURN_DURATION,
  easing: SMOOTH_EASING,
}
/** `direction` is +1 turning forward, -1 turning back. */
export function pageEntering(direction: 1 | -1) {
  return new Keyframe({
    0: {
      opacity: 0,
      transform: [{ translateX: PAGE_SLIDE_DISTANCE * direction }],
    },
    100: { opacity: 1, transform: [{ translateX: 0 }], easing: SMOOTH_EASING },
  }).duration(PAGE_TURN_DURATION)
}

/** The mirror of {@link pageEntering}: a page leaves by the side it came from. */
export function pageExiting(direction: 1 | -1) {
  return new Keyframe({
    0: { opacity: 1, transform: [{ translateX: 0 }] },
    100: {
      opacity: 0,
      transform: [{ translateX: PAGE_SLIDE_DISTANCE * direction }],
      easing: SMOOTH_EASING,
    },
  }).duration(PAGE_TURN_DURATION)
}

/**
 * transitions.dev "Success check", as an arrival for the control itself: fade
 * in, swing upright from 80°, and bob up 40px on a bouncy overshoot, all over
 * 500ms. The icon's own stroke-draw and blur are not part of it here.
 */
export const CHECK_DURATION = 500
export const CHECK_ROTATE_FROM = '80deg'
export const CHECK_BOB_DISTANCE = 40
export const CHECK_BOB_EASING = Easing.bezier(0.34, 1.35, 0.64, 1)
export const CHECK_ENTERING = new Keyframe({
  0: {
    opacity: 0,
    transform: [
      { translateY: CHECK_BOB_DISTANCE },
      { rotate: CHECK_ROTATE_FROM },
    ],
  },
  100: {
    opacity: 1,
    transform: [{ translateY: 0 }, { rotate: '0deg' }],
    easing: CHECK_BOB_EASING,
  },
}).duration(CHECK_DURATION)

/**
 * A keyboard action arriving. Short travel on purpose: a longer rise reads as
 * the button coming out from behind the keyboard, and the fade carries the
 * arrival instead.
 */
export const ACTION_ENTER_TRAVEL = 12
export const ACTION_ENTER_DURATION = 300
export const ACTION_ENTER_TIMING = {
  duration: ACTION_ENTER_DURATION,
  easing: Easing.out(Easing.quad),
}

/**
 * The same arrival as a keyframe pair, for whatever stands in the action's
 * slot beside it: a notice that replaces the button has to come and go the
 * way the button does, or the swap reads as one of them glitching.
 *
 * Leaving is quicker than arriving, so the slot is clear before its next
 * occupant rises into it.
 */
export const ACTION_EXIT_DURATION = 150
export const ACTION_ENTERING = new Keyframe({
  0: { opacity: 0, transform: [{ translateY: ACTION_ENTER_TRAVEL }] },
  100: {
    opacity: 1,
    transform: [{ translateY: 0 }],
    easing: Easing.out(Easing.quad),
  },
}).duration(ACTION_ENTER_DURATION)
export const ACTION_EXITING = new Keyframe({
  0: { opacity: 1, transform: [{ translateY: 0 }] },
  100: {
    opacity: 0,
    transform: [{ translateY: ACTION_ENTER_TRAVEL }],
    easing: Easing.out(Easing.quad),
  },
}).duration(ACTION_EXIT_DURATION)
/**
 * The ring taking a slot something else is still leaving. Paced to that exit
 * so the two cross-fade rather than both standing at full strength; a fade
 * alone, since the waiting state keeps to fill and opacity with no travel.
 */
export const INDICATOR_ENTERING = new Keyframe({
  0: { opacity: 0 },
  100: { opacity: 1, easing: Easing.out(Easing.quad) },
}).duration(ACTION_EXIT_DURATION)
